import { MAX_LEVEL } from '../content/balance';
import { Emitter } from '../core/events';
import { Rng } from '../core/rng';
import type { MonsterDef } from '../sim/registry';
import { type AccountSave, collectibleBonuses, legacyRank, tickLifeEnergy } from './account';
import { CARDS, type GemSocket, addCard, awakenCard, etch } from './build';
import { type CharacterSave, type LifeSkill, SKILL_LEVEL_COST, SKILL_MAX_LEVEL, buildCharacter, freeSkillPoints, xpToNext } from './character';
import { addItem, addStack, countOf, pay, removeItem, removeStack } from './inventory';
import { type EquipItem, type GearItem, type Rarity, gearIlvl, itemDef } from './items';
import { type LootDrop, rollGear, rollLoot } from './loot';
import { type QuestDef, type QuestEvent, accept, complete, progress, questDef } from './quests';
import { type TemperOutcome, costFor, stepFor, temper } from './tempering';
import { accountBonus } from './bonuses';

export interface ProgressionEvents {
  xp: { amount: number; level: number; pct: number };
  levelUp: { level: number };
  loot: { drop: LootDrop; source: string };
  item: { id: string; count: number; name: string; rarity: Rarity };
  equipDrop: { item: EquipItem };
  questAccepted: { quest: QuestDef };
  questUpdated: { quest: string };
  questStep: { quest: string; hint?: string };
  questReady: { quest: string };
  questCompleted: { quest: QuestDef };
  wallet: { silver: number; gold: number };
  statsChanged: Record<string, never>;
  toast: { text: string; color?: string };
  legacyRank: { rank: number };
  collectible: { kind: string; id: string };
  bond: { npc: string; points: number; stage: number };
  lifeSkill: { skill: LifeSkill; level: number };
}

export const BOND_STAGES = [0, 100, 300, 600, 1000];
export const BOND_STAGE_NAMES = ['Stranger', 'Acquainted', 'Friendly', 'Trusted', 'Devoted'];
export const LIFE_SKILL_MAX = 10;
export const lifeXpToNext = (lvl: number): number => 100 + lvl * 80;

/**
 * Owns the account/character progression rules. The UI and game systems call
 * these methods; persistence is a separate concern (SaveStore).
 */
export class Progression extends Emitter<ProgressionEvents> {
  readonly rng: Rng;
  /** Per-instance battle item uses remaining. */
  instanceUses = new Map<string, number>();

  constructor(
    public account: AccountSave,
    public char: CharacterSave,
    seed = Date.now(),
  ) {
    super();
    this.rng = new Rng(seed);
  }

  get legacyRank(): number {
    return legacyRank(this.account.legacyXp);
  }

  build(opts: { pvp?: boolean } = {}): ReturnType<typeof buildCharacter> {
    const cb = collectibleBonuses(this.account);
    return buildCharacter(this.char, { legacyRank: this.legacyRank, collectHp: cb.hp, collectAtk: cb.atk, cards: this.account.cards }, opts);
  }

  // ------------------------------------------------------------ XP & levels

  xpPct(): number {
    const need = xpToNext(this.char.level);
    return Number.isFinite(need) ? Math.min(1, this.char.xp / need) : 1;
  }

  grantXp(amount: number): void {
    const c = this.char;
    if (amount <= 0) return;
    const before = this.legacyRank;
    this.account.legacyXp += Math.round(amount * 0.5);
    if (c.level < MAX_LEVEL) {
      c.xp += Math.round(amount);
      while (c.level < MAX_LEVEL && c.xp >= xpToNext(c.level)) {
        c.xp -= xpToNext(c.level);
        c.level++;
        this.emit('levelUp', { level: c.level });
        this.questEvent({ type: 'level', level: c.level });
      }
      if (c.level >= MAX_LEVEL) c.xp = 0;
    }
    const after = this.legacyRank;
    if (after > before) this.emit('legacyRank', { rank: after });
    this.emit('xp', { amount, level: c.level, pct: this.xpPct() });
  }

  /** XP for killing a monster, scaled by the level difference. */
  killXp(def: MonsterDef, monsterLevel: number): number {
    const diff = monsterLevel - this.char.level;
    const k = diff >= 0 ? 1 + Math.min(diff, 5) * 0.1 : Math.max(0.1, 1 + diff * 0.15);
    return Math.round(def.xp * (1 + monsterLevel * 0.35) * k);
  }

  /** A monster died and this player contributed: XP, personal loot, quests. */
  onKill(def: MonsterDef, monsterLevel: number, opts: { loot?: boolean; tags?: string[] } = {}): LootDrop | null {
    this.grantXp(this.killXp(def, monsterLevel));
    this.questEvent({ type: 'kill', target: def.id });
    this.char.counters.kills = (this.char.counters.kills ?? 0) + 1;
    if (def.grade === 'named' || def.grade === 'boss' || def.grade === 'raid') this.char.counters[`kill_${def.id}`] = (this.char.counters[`kill_${def.id}`] ?? 0) + 1;
    if (opts.loot === false || !def.loot) return null;
    const drop = rollLoot(def.loot, { level: monsterLevel, rng: this.rng });
    this.grantDrop(drop, def.name);
    return drop;
  }

  grantDrop(drop: LootDrop, source: string): void {
    const a = this.account;
    a.wallet.silver += drop.silver;
    a.wallet.gold += drop.gold;
    for (const it of drop.items) this.giveItem(it.id, it.count);
    for (const eq of drop.equip) {
      if (!addItem(this.char, eq)) this.mail('Overflow', `Your bag was full: ${eq.name}`, [], 0, 0, eq);
      else this.emit('equipDrop', { item: eq });
    }
    for (const id of drop.cards) {
      const r = addCard(a.cards, id);
      const card = CARDS[id];
      if (card) this.emit('toast', { text: `${r === 'new' ? 'New card' : 'Card duplicate'}: ${card.name}`, color: '#ffd070' });
    }
    if (drop.silver || drop.gold) this.emit('wallet', { silver: a.wallet.silver, gold: a.wallet.gold });
    this.emit('loot', { drop, source });
  }

  giveItem(id: string, count: number): number {
    const added = addStack(this.char, id, count);
    const def = itemDef(id);
    if (added > 0) this.emit('item', { id, count: added, name: def.name, rarity: def.rarity });
    if (added < count) this.mail('Overflow', `Your bag was full: ${def.name} x${count - added}`, [{ id, count: count - added }], 0, 0);
    this.questEvent({ type: 'collect', target: id, count: added });
    return added;
  }

  mail(from: string, subject: string, items: { id: string; count: number }[], silver: number, gold: number, equip?: EquipItem): void {
    this.account.mail.unshift({ id: `m_${Date.now()}_${Math.floor(Math.random() * 1e6)}`, from, subject, body: '', items, ...(equip ? { equip: [equip] } : {}), silver, gold, read: false, at: Date.now() });
  }

  // ------------------------------------------------------------ quests

  questEvent(ev: QuestEvent): void {
    const r = progress(this.char, ev);
    for (const id of r.updated) this.emit('questUpdated', { quest: id });
    for (const s of r.stepsAdvanced) this.emit('questStep', { quest: s.quest, hint: s.hint });
    for (const id of r.readyToTurnIn) {
      this.emit('questReady', { quest: id });
      const q = questDef(id);
      // Auto-complete quests whose turn-in is "self" (e.g. objectives that end in the field).
      if (q.turnIn === 'self') this.completeQuest(id);
    }
  }

  acceptQuest(id: string): boolean {
    if (!accept(this.char, id)) return false;
    this.emit('questAccepted', { quest: questDef(id) });
    return true;
  }

  completeQuest(id: string): QuestDef | null {
    const q = complete(this.char, id);
    if (!q) return null;
    const r = q.rewards;
    if (r.silver) this.account.wallet.silver += r.silver;
    if (r.gold) this.account.wallet.gold += r.gold;
    for (const it of r.items ?? []) this.giveItem(it.id, it.count);
    if (r.gear) {
      const g = rollGear(Math.max(1, q.level), r.gear, this.rng);
      if (addItem(this.char, g)) this.emit('equipDrop', { item: g });
    }
    if (r.skillPoints) this.char.bonusSkillPoints += r.skillPoints;
    if (r.card) addCard(this.account.cards, r.card);
    if (r.bond && q.turnIn !== 'self') this.addBond(q.turnIn, r.bond);
    this.emit('questCompleted', { quest: q });
    this.grantXp(r.xp);
    for (const f of r.flags ?? []) this.questEvent({ type: 'flag', target: f });
    this.emit('wallet', { silver: this.account.wallet.silver, gold: this.account.wallet.gold });
    return q;
  }

  setFlag(flag: string, value: number | boolean | string = true): void {
    this.char.flags[flag] = value;
    this.questEvent({ type: 'flag', target: flag });
  }

  // ------------------------------------------------------------ skills

  canLevelSkill(skillId: string): boolean {
    const lvl = this.char.skillLevels[skillId] ?? 1;
    if (lvl >= SKILL_MAX_LEVEL) return false;
    return freeSkillPoints(this.char) >= (SKILL_LEVEL_COST[lvl + 1] ?? 99);
  }

  levelSkill(skillId: string, delta: 1 | -1): boolean {
    const lvl = this.char.skillLevels[skillId] ?? 1;
    if (delta > 0) {
      if (!this.canLevelSkill(skillId)) return false;
      this.char.skillLevels[skillId] = lvl + 1;
    } else {
      if (lvl <= 1) return false;
      this.char.skillLevels[skillId] = lvl - 1;
      // Drop tripods above the unlocked tier.
      const tiers = [4, 7, 10].filter((t) => lvl - 1 >= t).length;
      const tp = this.char.tripods[skillId];
      if (tp) for (let i = tiers; i < 3; i++) tp[i] = null;
    }
    this.emit('statsChanged', {});
    return true;
  }

  setTripod(skillId: string, tier: 0 | 1 | 2, tripodId: string | null): void {
    const tp = (this.char.tripods[skillId] ??= [null, null, null]);
    tp[tier] = tripodId;
    this.emit('statsChanged', {});
  }

  // ------------------------------------------------------------ tempering

  temperPreview(g: GearItem): { step: ReturnType<typeof stepFor>; cost: ReturnType<typeof costFor> | null; affordable: boolean } {
    const step = stepFor(g);
    if (!step) return { step: null, cost: null, affordable: false };
    const base = costFor(g, step);
    const cost = { ...base, silver: Math.round(base.silver * Math.max(0, 1 - accountBonus('temperSilver'))) };
    const weapon = g.slot === 'weapon';
    const affordable =
      countOf(this.char, 'aether_shard') >= cost.shards &&
      countOf(this.char, weapon ? 'ore_weapon' : 'ore_armor') >= cost.ore &&
      countOf(this.char, 'leap_crystal') >= cost.leap &&
      countOf(this.char, 'fusion_alloy') >= cost.fusion &&
      this.account.wallet.silver >= cost.silver &&
      this.account.wallet.gold >= cost.gold;
    return { step, cost, affordable };
  }

  /** Pays and performs one tempering attempt on an equipped or bagged gear piece. */
  temperGear(g: GearItem, catalysts = 0, tome = false): TemperOutcome | null {
    const pv = this.temperPreview(g);
    if (!pv.step || !pv.cost || !pv.affordable) return null;
    const cat = Math.min(catalysts, countOf(this.char, 'catalyst'));
    const useTome = tome && countOf(this.char, 'forge_tome') > 0 && pv.step.plus >= 11;
    const c = pv.cost;
    const ok = pay(this.char, this.account, {
      silver: c.silver,
      gold: c.gold,
      items: {
        aether_shard: c.shards,
        [g.slot === 'weapon' ? 'ore_weapon' : 'ore_armor']: c.ore,
        leap_crystal: c.leap,
        fusion_alloy: c.fusion,
        catalyst: cat,
        forge_tome: useTome ? 1 : 0,
      },
    });
    if (!ok) return null;
    const out = temper(g, () => this.rng.next(), cat, useTome);
    this.char.counters.temperAttempts = (this.char.counters.temperAttempts ?? 0) + 1;
    if (out.success) this.char.counters[`temper_plus_${gearIlvl(g)}`] = 1;
    this.emit('statsChanged', {});
    this.emit('wallet', { silver: this.account.wallet.silver, gold: this.account.wallet.gold });
    return out;
  }

  // ------------------------------------------------------------ build systems

  readSigilBook(bookId: string): boolean {
    const def = itemDef(bookId);
    if (def.kind !== 'book' || !def.ref) return false;
    const cur = this.char.sigilBooks[def.ref] ?? 0;
    if (cur >= 20 || !removeStack(this.char, bookId, 1)) return false;
    this.char.sigilBooks[def.ref] = Math.min(20, cur + 5);
    this.emit('statsChanged', {});
    return true;
  }

  setActiveSigils(ids: string[]): void {
    this.char.activeSigils = ids.slice(0, 5);
    this.emit('statsChanged', {});
  }

  socketGem(index: number, gemItemId: string, skillId: string): boolean {
    const def = itemDef(gemItemId);
    if (def.kind !== 'gem' || !def.level) return false;
    if (index < 0 || index >= this.char.gems.length) return false;
    if (!removeStack(this.char, gemItemId, 1)) return false;
    const old = this.char.gems[index];
    if (old) addStack(this.char, `gem_${old.type}_${old.level}`, 1);
    this.char.gems[index] = { type: def.ref as GemSocket['type'], level: def.level, skillId };
    this.emit('statsChanged', {});
    return true;
  }

  unsocketGem(index: number): boolean {
    const old = this.char.gems[index];
    if (!old) return false;
    if (addStack(this.char, `gem_${old.type}_${old.level}`, 1) <= 0) return false;
    this.char.gems[index] = null;
    this.emit('statsChanged', {});
    return true;
  }

  /** Fuses 3 gems of one level into one of the next level (costs silver). */
  fuseGems(type: 'dmg' | 'cd', level: number): boolean {
    if (level >= 7) return false;
    const id = `gem_${type}_${level}`;
    if (countOf(this.char, id) < 3 || !pay(this.char, this.account, { silver: 200 * level * level, items: { [id]: 3 } })) return false;
    this.giveItem(`gem_${type}_${level + 1}`, 1);
    return true;
  }

  setDeckCard(index: number, cardId: string | null): void {
    const deck = this.account.cards.deck;
    if (cardId) for (let i = 0; i < deck.length; i++) if (deck[i] === cardId) deck[i] = null;
    deck[index] = cardId;
    this.emit('statsChanged', {});
  }

  awaken(cardId: string): boolean {
    const ok = awakenCard(this.account.cards, cardId);
    if (ok) this.emit('statsChanged', {});
    return ok;
  }

  /** Rune stone etching costs silver per attempt. */
  etchStone(uid: string, line: number): boolean | null {
    const stone = this.char.equipment.stone?.uid === uid ? this.char.equipment.stone : this.char.inventory.items.find((i) => i.uid === uid && i.kind === 'stone');
    if (!stone || stone.kind !== 'stone') return null;
    if (!pay(this.char, this.account, { silver: 150 })) return null;
    const r = etch(stone, line, () => this.rng.next());
    this.emit('statsChanged', {});
    return r;
  }

  // ------------------------------------------------------------ bonds, collectibles, life skills

  bondStage(npc: string): number {
    const pts = this.char.bonds[npc] ?? 0;
    let s = 0;
    BOND_STAGES.forEach((t, i) => {
      if (pts >= t) s = i;
    });
    return s;
  }

  addBond(npc: string, points: number): void {
    const before = this.bondStage(npc);
    this.char.bonds[npc] = (this.char.bonds[npc] ?? 0) + points;
    const after = this.bondStage(npc);
    this.emit('bond', { npc, points: this.char.bonds[npc] ?? 0, stage: after });
    if (after > before) {
      // Stage rewards: skill potions and silver.
      if (after >= 2) this.giveItem('skill_potion', 1);
      this.account.wallet.silver += 500 * after;
      this.emit('toast', { text: `Bond deepened: ${BOND_STAGE_NAMES[after]}`, color: '#ff90c0' });
    }
  }

  collect(kind: 'seeds' | 'hearts' | 'vistas' | 'lore' | 'fieldBosses', id: string): boolean {
    const list = this.account.collectibles[kind];
    if (list.includes(id)) return false;
    list.push(id);
    this.emit('collectible', { kind, id });
    if (kind === 'hearts') this.char.bonusSkillPoints += 2;
    if (kind === 'seeds' && list.length % 10 === 0) this.char.bonusSkillPoints += 2;
    this.emit('statsChanged', {});
    return true;
  }

  lifeEnergy(): number {
    tickLifeEnergy(this.account);
    return this.account.lifeEnergy;
  }

  spendLifeEnergy(n: number): boolean {
    tickLifeEnergy(this.account);
    if (this.account.lifeEnergy < n) return false;
    this.account.lifeEnergy -= n;
    return true;
  }

  lifeSkillXp(skill: LifeSkill, xp: number): void {
    const ls = (this.char.lifeSkills[skill] ??= { level: 1, xp: 0 });
    if (ls.level >= LIFE_SKILL_MAX) return;
    ls.xp += xp;
    while (ls.level < LIFE_SKILL_MAX && ls.xp >= lifeXpToNext(ls.level)) {
      ls.xp -= lifeXpToNext(ls.level);
      ls.level++;
      this.emit('lifeSkill', { skill, level: ls.level });
    }
  }

  // ------------------------------------------------------------ battle items

  /** Resets per-instance battle item limits (entering an instance or resupplying). */
  resetInstanceUses(): void {
    this.instanceUses.clear();
    for (const id of this.char.battleSlots) {
      if (!id) continue;
      const def = itemDef(id);
      if (def.battle) this.instanceUses.set(id, def.battle.limit);
    }
  }

  /** Consumes one battle item if the slot has stock and uses left. */
  consumeBattleItem(id: string, limited: boolean): boolean {
    if (countOf(this.char, id) <= 0) return false;
    if (limited) {
      const left = this.instanceUses.get(id) ?? itemDef(id).battle?.limit ?? 0;
      if (left <= 0) return false;
      this.instanceUses.set(id, left - 1);
    }
    removeStack(this.char, id, 1);
    return true;
  }

  // ------------------------------------------------------------ misc

  discardItem(uid: string): boolean {
    return removeItem(this.char, uid) !== null;
  }
}
